<script>
import baseMixin from '@/mixins/baseMixin'
import { mapActions, mapGetters } from 'vuex'
import { TimesheetOperationType } from '@/shared/constants/timesheet/TimesheetOperationType'
import generateBreakMinutesList from '@/utils/generate-break-minutes-list.js'
import { RateUnitType } from '@/shared/constants/booking/RateUnitType'
import SideDrawerDialog from '@/components/general/side-drawer-dialog.vue'
import timesheetMixin from '@/mixins/timesheetMixin'
import { minutesToHoursMinsString } from '@/utils/minutes-to-hours-mins-string'
import { orderBy } from 'lodash'
import ModifyTimesheetPayload from '@/models/timesheets/modifyTimesheetPayload'
import dayjs from '@/services/date/index'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import TimesheetChangeTimeCardDetails from './timesheet-change-time-card-details.vue'

export default {
  name: 'TimeSheetChangeTimeCard',
  components: {
    SideDrawerDialog,
    TimesheetChangeTimeCardDetails,
  },
  mixins: [baseMixin, timesheetMixin],
  model: {
    prop: 'show',
    event: 'showUpdated',
  },
  props: {
    show: {
      type: Boolean,
    },
    timesheet: {
      default: () => null,
      type: Object,
    },
  },
  data() {
    return {
      showDialog: false,
      startTime: '',
      endTime: '',
      payOptionType: '',
      isValid: true,
      rules: {
        required: (v) => !!v || this.$t('input.inputRequiredValidationText'),
        startTimeAfterEnd: () =>
          this.startTime
            ? !this.isStartInvalid ||
              this.$t('input.startTimeBeforeEndValidationText')
            : true,
        endTimeBeforeStart: () =>
          this.endTime
            ? !this.isEndInvalid ||
              this.$t('input.endTimeBeforeStartValidationText')
            : true,
      },
      TimesheetOperationType,
      breaks: [],
      breakMinutes: 0,
    }
  },
  computed: {
    ...mapGetters({
      isLoadingCRUD: 'timesheets/isLoadingCRUD',
    }),
    isStartInvalid() {
      return this.startTime > this.endTime
    },
    isEndInvalid() {
      return this.endTime < this.startTime
    },
    isUnitClassification() {
      return this.timesheet?.unitType === RateUnitType.UNIT
    },
    isHourlyClassification() {
      return this.timesheet?.unitType === RateUnitType.HOURLY
    },
  },
  watch: {
    async show(v, o) {
      this.showDialog = this.show
      if (this.show) {
        this.resetForm()
      }
    },
    startTime(v, o) {
      this.setBreaksOnTimeUpdate()
    },
    endTime(v, o) {
      this.setBreaksOnTimeUpdate()
    },
  },
  beforeDestroy() {
    this.resetForm()
  },
  methods: {
    ...mapActions({
      submitTimesheetsForApproval: 'timesheets/submitTimesheetsForApproval',
    }),
    resetForm() {
      if (!this.timesheet) return

      const bookingDetails = this.getModifiedOrOriginalBookingDetails(
        this.timesheet,
        true
      )

      this.startTime = bookingDetails.startTime
      this.endTime = bookingDetails.endTime
      this.payOptionType = bookingDetails.payOptionType

      this.initBreaksList(bookingDetails.breaks)
    },
    validate() {
      return this.$refs.timeChangeForm.validate()
    },
    areBreakMinutesGreaterThanBookingDuration(breakMinutes) {
      const durationInMinutes = dayjs(this.endTime, DateFormatToken.HHmm).diff(
        dayjs(this.startTime, DateFormatToken.HHmm),
        'minutes'
      )

      return breakMinutes > durationInMinutes
    },
    /**
     * Attempts to use the already selected break minutes value when updating either the start or end time.
     * If the selected breaks are greater than the new booking duration, it defaults to 0 break mins
     */
    setBreaksOnTimeUpdate() {
      let preselectedMins = this.breakMinutes

      if (this.areBreakMinutesGreaterThanBookingDuration(this.breakMinutes)) {
        preselectedMins = 0
      }

      this.initBreaksList(preselectedMins)
    },
    async save() {
      this.isValid = this.validate()
      if (!this.isValid) return false

      const modifyPayload = new ModifyTimesheetPayload({ ...this.timesheet })

      modifyPayload.newStartTimeLocal = this.startTime
      modifyPayload.newEndTimeLocal = this.endTime
      modifyPayload.newPayOptionType = this.payOptionType

      // Ensure the existing break minutes on the booking are returned
      // if minutes aren't updated. If breakMinutes was null, it'll be
      // returns as 0
      modifyPayload.newBreakMinutes =
        this.breakMinutes >= 0 && this.breakMinutes !== null
          ? this.breakMinutes
          : this.timesheet.breakMinutes

      const payload = this.mapModifyTimesheetChangesToPayload(modifyPayload)

      const result = await this.submitTimesheetsForApproval([payload])

      if (!result?.isSuccess) return

      this.$emit('submitted', { result, timesheets: [this.timesheet] })

      this.closeDialog()
    },
    updateBreaks(value) {
      this.breakMinutes = value
    },
    closeDialog() {
      this.$emit('showUpdated', false)
    },
    initBreaksList(initBreaks = 0) {
      if (!this.startTime || !this.endTime) return

      this.breaks = generateBreakMinutesList(this.startTime, this.endTime)

      if (initBreaks > 0) {
        const foundBreak = this.breaks.find((x) => x.id === initBreaks)
        if (foundBreak) return (this.breakMinutes = initBreaks)

        // Push custom breaks to the list
        this.breaks.push({
          id: initBreaks,
          text: minutesToHoursMinsString(initBreaks),
        })

        // Sort breaks list
        this.breaks = orderBy(this.breaks, ['id'], ['asc'])

        // Init break minutes to custom value
        return (this.breakMinutes = initBreaks)
      }

      // Initialise breaks at 0 mins when initialising the breaks list
      this.breakMinutes = this.breaks.length > 0 ? this.breaks[0].id : 0
    },
  },
}
</script>

<template>
  <SideDrawerDialog
    v-if="timesheet"
    v-model="showDialog"
    :title="$t('timesheets.timesheetChangeTimeTitle')"
    :description="$t('timesheets.timesheetChangeTimeSubtitle')"
    :xl="4"
    :lg="6"
    :md="9"
    data-testid="timesheet-change-time-dialog"
    @closeDialog="closeDialog"
  >
    <div class="h-100">
      <TimesheetChangeTimeCardDetails :timesheet="timesheet" class="mb-8" />

      <v-form
        ref="timeChangeForm"
        lazy-validation
        :value="isValid"
        autocomplete="off"
      >
        <div :class="['d-flex justify-space-between align-center mb-2']">
          <div id="start-time-input_container" class="time-container">
            <v-text-field
              id="start-time-input"
              v-model="startTime"
              type="time"
              outlined
              color="primary"
              :label="$t('input.startTimeLabel')"
              :rules="[rules.startTimeAfterEnd]"
            ></v-text-field>
          </div>

          <v-divider class="date-range-input-divider" />

          <div id="end-time-input_container" class="time-container">
            <v-text-field
              id="end-time-input"
              v-model="endTime"
              type="time"
              outlined
              color="primary"
              :label="$t('input.endTimeLabel')"
              :rules="[rules.endTimeBeforeStart]"
            ></v-text-field>
          </div>
        </div>

        <div class="mb-2">
          <v-select
            id="timesheet-change-time-card-breaks-select"
            ref="timesheet-change-time-card-breaks-select"
            data-testid="timesheet-change-time-card-breaks-select"
            :label="$t('timesheets.breaksLabel')"
            :value="breakMinutes"
            :items="breaks"
            outlined
            item-text="text"
            item-value="id"
            :return-object="false"
            @change="updateBreaks"
          />
        </div>

        <div id="pay-option-types-select_container" class="mb-2">
          <v-select
            v-model="payOptionType"
            outlined
            label="Pay Options"
            :items="timesheet.payOptionTypes"
          >
          </v-select>
        </div>
      </v-form>
    </div>
    <template v-slot:actions="slotProps">
      <div class="d-flex justify-end align-center w-100">
        <v-btn
          data-testid="timesheet-change-time-card-cancel-btn"
          class="mr-2"
          text
          large
          @click="slotProps.close"
          >{{ $t('timesheets.timesheetChangeTimeCancelButtonText') }}</v-btn
        >
        <v-btn
          data-testid="timesheet-change-time-card-submit-btn"
          depressed
          large
          color="primary"
          :disabled="!isValid"
          :loading="isLoadingCRUD"
          @click="save"
        >
          {{ $t('timesheets.timesheetChangeTimeUpdateButtonText') }}
        </v-btn>
      </div>
    </template>
  </SideDrawerDialog>
</template>

<style lang="scss" scoped>
.time-container {
  width: 42.5%;
}

.date-range-input-divider {
  width: 5%;
  margin-right: 16px !important;
  margin-bottom: 32px !important;
  margin-left: 16px !important;
  border-style: dotted;
}
</style>
